import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import CommentThread from '../components/CommentThread';
import EmojiTextarea from '../components/EmojiTextarea';

function formatDate(iso) {
  if (!iso) return '';
  return iso.replace('T', ' ').slice(0, 16);
}

function formatBytes(bytes) {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
}

const NEW_POST_WINDOW_MS = 24 * 60 * 60 * 1000;

function isNewPost(iso) {
  if (!iso) return false;
  const createdAt = new Date(iso.endsWith('Z') ? iso : `${iso}Z`);
  return Date.now() - createdAt.getTime() < NEW_POST_WINDOW_MS;
}

export default function Board() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [writing, setWriting] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [files, setFiles] = useState([]);
  const [commentText, setCommentText] = useState('');
  const fileInputRef = useRef(null);

  const loadPosts = useCallback(async () => {
    const res = await api.get('/board/posts');
    setPosts(res.data);
  }, []);

  const loadDetail = useCallback(async (postid) => {
    const res = await api.get(`/board/posts/${postid}`);
    setDetail(res.data);
  }, []);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  const openWrite = () => {
    setWriting(true);
    setTitle('');
    setContent('');
    setFiles([]);
  };

  const submitPost = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) return;
    const res = await api.post('/board/posts', { title: title.trim(), content: content.trim() });
    const postid = res.data.postid;
    if (files.length > 0) {
      const form = new FormData();
      files.forEach((f) => form.append('files', f));
      await api.post(`/board/posts/${postid}/attachments`, form);
    }
    setWriting(false);
    await loadPosts();
    setSelectedId(postid);
  };

  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files || []));
  };

  const removeSelectedFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const downloadAttachment = async (attachid, filename) => {
    const res = await api.get(`/board/attachments/${attachid}/download`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const deleteAttachment = async (attachid) => {
    await api.delete(`/board/attachments/${attachid}`);
    await loadDetail(selectedId);
    await loadPosts();
  };

  const submitComment = async () => {
    if (!commentText.trim()) return;
    await api.post(`/board/posts/${selectedId}/comments`, { content: commentText.trim() });
    setCommentText('');
    await loadDetail(selectedId);
    await loadPosts();
  };

  const handleReply = async (parentid, text) => {
    await api.post(`/board/posts/${selectedId}/comments`, { content: text, parentid });
    await loadDetail(selectedId);
    await loadPosts();
  };

  const handleCommentEdit = async (commentid, text) => {
    await api.put(`/board/comments/${commentid}`, { content: text });
    await loadDetail(selectedId);
  };

  const handleCommentDelete = async (commentid) => {
    await api.delete(`/board/comments/${commentid}`);
    await loadDetail(selectedId);
    await loadPosts();
  };

  const handleDeletePost = async () => {
    await api.delete(`/board/posts/${selectedId}`);
    setSelectedId(null);
    setDetail(null);
    await loadPosts();
  };

  if (selectedId && detail) {
    const canManage = user?.teachid === detail.teachid || user?.role === 'admin' || user?.role === 'staff';
    return (
      <div className="card board-detail">
        <button className="btn-ghost" onClick={() => { setSelectedId(null); setDetail(null); }}>
          ← 목록으로
        </button>
        <h3 className="board-post-title">{detail.title}</h3>
        <div className="board-post-meta">
          {detail.author_name} · {formatDate(detail.createat)}
        </div>
        <div className="board-post-content">{detail.content}</div>

        {detail.attachments.length > 0 && (
          <div className="attachment-list">
            {detail.attachments.map((a) => (
              <div key={a.attachid} className="attachment-item">
                <button className="btn-link attachment-name" onClick={() => downloadAttachment(a.attachid, a.filename)}>
                  📎 {a.filename}
                </button>
                <span className="attachment-size">{formatBytes(a.filesize)}</span>
                {canManage && (
                  <button className="btn-link" onClick={() => deleteAttachment(a.attachid)}>삭제</button>
                )}
              </div>
            ))}
          </div>
        )}

        {canManage && (
          <div className="comment-actions">
            <button className="btn-link" onClick={handleDeletePost}>게시글 삭제</button>
          </div>
        )}

        <div className="comment-list">
          <h4>댓글 {detail.comments.length > 0 ? `(${countAll(detail.comments)})` : ''}</h4>
          {detail.comments.map((c) => (
            <CommentThread
              key={c.commentid}
              comment={c}
              onReply={handleReply}
              onEdit={handleCommentEdit}
              onDelete={handleCommentDelete}
            />
          ))}
          <div className="comment-reply-form">
            <EmojiTextarea
              rows={2}
              placeholder="댓글을 입력하세요"
              value={commentText}
              onChange={setCommentText}
            />
            <button className="btn-primary btn-inline" onClick={submitComment}>댓글 등록</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="board-header">
        <h3>게시판</h3>
        <button className="btn-primary btn-inline" onClick={openWrite}>글쓰기</button>
      </div>

      {writing && (
        <form className="board-write-form" onSubmit={submitPost}>
          <input
            className="field-input"
            type="text"
            placeholder="제목"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <EmojiTextarea
            className="field-input"
            rows={4}
            placeholder="내용을 입력하세요"
            value={content}
            onChange={setContent}
          />

          <div className="file-picker">
            <button type="button" className="btn-ghost" onClick={() => fileInputRef.current?.click()}>
              파일 첨부
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            {files.length > 0 && (
              <div className="file-picker-list">
                {files.map((f, i) => (
                  <span key={i} className="file-chip">
                    {f.name}
                    <button type="button" className="file-chip-remove" onClick={() => removeSelectedFile(i)}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="comment-actions">
            <button className="btn-primary btn-inline" type="submit">등록</button>
            <button className="btn-ghost" type="button" onClick={() => setWriting(false)}>취소</button>
          </div>
        </form>
      )}

      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th>제목</th>
              <th>작성자</th>
              <th>작성일</th>
              <th>댓글</th>
              <th>첨부</th>
            </tr>
          </thead>
          <tbody>
            {posts.map((p) => (
              <tr key={p.postid} className="board-row" onClick={() => setSelectedId(p.postid)}>
                <td className="board-title-cell">
                  {p.title}
                  {isNewPost(p.createat) && <span className="board-new-badge">새글</span>}
                </td>
                <td>{p.author_name}</td>
                <td>{formatDate(p.createat)}</td>
                <td>{p.comment_count}</td>
                <td>{p.attachment_count > 0 ? `📎 ${p.attachment_count}` : ''}</td>
              </tr>
            ))}
            {posts.length === 0 && (
              <tr>
                <td colSpan="5" className="empty-row">등록된 게시글이 없습니다.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function countAll(comments) {
  return comments.reduce((sum, c) => sum + 1 + countAll(c.replies || []), 0);
}
